import { useState, useEffect, useRef } from "react";
import { ChevronDown, Check, Clock, HelpCircle, Award } from "lucide-react";

const CustomProfileSelect = ({ options, value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  // Ensure options is always an array
  const optionsArray = Array.isArray(options) ? options : [];

  // Initialize selectedOption properly using useEffect to keep it in sync with value prop
  const [selectedOption, setSelectedOption] = useState(null);

  useEffect(() => {
    // Update selectedOption whenever value or options change
    if (value && optionsArray.length > 0) {
      const found = optionsArray.find(
        (option) =>
          option &&
          option._id &&
          (option._id === value || option._id.toString() === value.toString()),
      );

      // Debug the matching process
      if (!found) {
        console.log("No matching option found for value:", value);
        console.log(
          "Available options:",
          optionsArray.map((o) => o._id),
        );
      }

      setSelectedOption(found || null);
    } else {
      setSelectedOption(null);
    }
  }, [value, optionsArray]);

  const filteredOptions = optionsArray.filter(
    (option) =>
      option &&
      option.basic &&
      option.basic.name &&
      option.basic.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleOptionClick = (option) => {
    if (option && option._id) {
      // Call onChange with the ID and the full option object
      onChange(option._id, option);

      // Close dropdown and clear search
      setIsOpen(false);
      setSearchTerm("");
    }
  };

  // Helper function to convert difficulty level number to text
  const getDifficultyText = (level) => {
    if (level === undefined || level === null) return "Unknown";

    const levels = {
      1: "Easy",
      2: "Moderate",
      3: "Intermediate",
      4: "Advanced",
      5: "Expert",
    };
    return levels[level] || "Unknown";
  };

  return (
    <div className="relative m-auto" ref={dropdownRef}>
      {/* Select header */}
      <div
        className="flex items-center justify-between w-full p-3 border border-gray-300 rounded-lg bg-white cursor-pointer hover:border-blue-500"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex-1">
          {selectedOption && selectedOption.basic ? (
            <div className="flex flex-col lg:flex-row items-center">
              <span className="font-medium text-gray-900 self-start">
                {selectedOption.basic.name}
              </span>
              <div className="flex items-center lg:ml-4 space-x-4 text-sm text-gray-500">
                <div className="flex items-center">
                  <Clock size={14} className="mr-1" />
                  <span>{selectedOption.parameters.duration} min</span>
                </div>
                <div className="flex items-center">
                  <HelpCircle size={14} className="mr-1" />
                  <span>
                    {selectedOption.parameters.question_count} questions
                  </span>
                </div>
                <div className="flex items-center">
                  <Award size={14} className="mr-1" />
                  <span>
                    {getDifficultyText(
                      selectedOption.parameters.difficulty_level,
                    )}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <span className="text-gray-500">
              Select an assessment profile...
            </span>
          )}
        </div>
        <ChevronDown
          className={`transition-transform duration-200 ${isOpen ? "transform rotate-180" : ""}`}
        />
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg">
          {/* Search box */}
          <div className="p-2 border-b">
            <input
              type="text"
              placeholder="Search profiles..."
              className="w-full p-2 border border-gray-300 rounded"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* Options list */}
          <div className="max-h-60 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <div
                  key={option._id}
                  className={`flex items-center p-3 cursor-pointer hover:bg-blue-50 ${
                    selectedOption &&
                    option._id &&
                    selectedOption._id &&
                    (selectedOption._id === option._id ||
                      selectedOption._id.toString() === option._id.toString())
                      ? "bg-blue-50"
                      : ""
                  }`}
                  onClick={() => handleOptionClick(option)}
                >
                  <div className="flex-1">
                    <div className="font-medium">{option.basic.name}</div>
                    <div className="flex mt-1 space-x-4 text-xs text-gray-500">
                      <div>{option.parameters.duration} min</div>
                      <div>{option.parameters.question_count} questions</div>
                      <div>
                        {getDifficultyText(option.parameters.difficulty_level)}
                      </div>
                    </div>
                    {option.basic.scope !== "custom" && (
                      <div className="mt-1 text-xs text-blue-600">
                        Scope: {option.basic.scope}
                      </div>
                    )}
                  </div>
                  {selectedOption &&
                    option._id &&
                    selectedOption._id &&
                    (selectedOption._id === option._id ||
                      selectedOption._id.toString() ===
                        option._id.toString()) && (
                      <Check className="text-blue-600" />
                    )}
                </div>
              ))
            ) : (
              <div className="p-3 text-center text-gray-500">
                No matching profiles found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomProfileSelect;
